import { identity } from '$core/identity';
import {
  listFriends,
  listIncomingRequests,
  listOutgoingRequests,
  listMyGroups
} from './api';
import type {
  Friend,
  FriendRequestWithUser,
  Group
} from './types';

/**
 * Store reactivo de la capa social (M4.8d).
 *
 * Mantiene en memoria: amigos, solicitudes entrantes/salientes y grupos.
 * Todas las operaciones van a través de la API de features/social/api.ts.
 *
 * La UI observa los getters y $state. El store se refresca manualmente
 * (refreshAll) o al cambiar de usuario (via guard de userId).
 */
class SocialStore {
  friends = $state<Friend[]>([]);
  incoming = $state<FriendRequestWithUser[]>([]);
  outgoing = $state<FriendRequestWithUser[]>([]);
  groups = $state<Group[]>([]);

  loading = $state(false);
  lastError = $state<string | null>(null);

  private loadedUserId: string | null = null;
  private refreshInFlight: Promise<void> | null = null;

  get pendingCount(): number {
    return this.incoming.length;
  }

  get hasPending(): boolean {
    return this.incoming.length > 0;
  }

  get friendsCount(): number {
    return this.friends.length;
  }

  get groupsCount(): number {
    return this.groups.length;
  }

  /**
   * Limpia el estado local. Se llama al cambiar de usuario.
   */
  clear(): void {
    this.friends = [];
    this.incoming = [];
    this.outgoing = [];
    this.groups = [];
    this.lastError = null;
    this.loadedUserId = null;
  }

  /**
   * Refresca todo. Solo si está autenticado y el userId es válido.
   * Si ya se cargó para este usuario, no repite (salvo force=true).
   *
   * Evita solapar refreshes concurrentes: si ya hay uno en vuelo,
   * devuelve esa misma promesa.
   */
  async refreshAll(force = false): Promise<void> {
    if (identity.identity.kind !== 'authenticated') {
      this.clear();
      return;
    }
    const uid = identity.userId;
    if (!uid) return;

    if (!force && this.loadedUserId === uid) {
      return;
    }

    if (this.refreshInFlight) {
      return this.refreshInFlight;
    }

    this.loadedUserId = uid;
    this.loading = true;
    this.lastError = null;

    const task = (async () => {
      try {
        const [friends, incoming, outgoing, groups] = await Promise.all([
          listFriends(),
          listIncomingRequests(),
          listOutgoingRequests(),
          listMyGroups()
        ]);
        this.friends = friends;
        this.incoming = incoming;
        this.outgoing = outgoing;
        this.groups = groups;
      } catch (err) {
        this.lastError = err instanceof Error ? err.message : String(err);
      } finally {
        this.loading = false;
        this.refreshInFlight = null;
      }
    })();

    this.refreshInFlight = task;
    return task;
  }

  async refreshFriends(): Promise<void> {
    this.friends = await listFriends();
  }

  async refreshRequests(): Promise<void> {
    const [incoming, outgoing] = await Promise.all([
      listIncomingRequests(),
      listOutgoingRequests()
    ]);
    this.incoming = incoming;
    this.outgoing = outgoing;
  }

  async refreshGroups(): Promise<void> {
    this.groups = await listMyGroups();
  }
}

export const socialStore = new SocialStore();